import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  Modal,
  Divider
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import LanguageIcon from '@mui/icons-material/Language';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import PhoneIcon from '@mui/icons-material/Phone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../services/userContext';
import PreferencesModal from '../shared/PreferencesModal';
import UserDetailsModal from '../shared/UserDetailsModal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const HubLayout = ({ children }) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const [isUserDetailsModalOpen, setIsUserDetailsModalOpen] = useState(false);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [localTimeLeft, setLocalTimeLeft] = useState(0);
  const [hourToastShown, setHourToastShown] = useState(false);
  const [halfHourToastShown, setHalfHourToastShown] = useState(false);
  const [tenMinToastShown, setTenMinToastShown] = useState(false);
  const [clockColor, setClockColor] = useState('#007A7A');

  // Session countdown logic
  useEffect(() => {
    const calculateTimeLeft = () => {
      const expiryTime = localStorage.getItem('sessionExpiry');
      if (expiryTime) {
        const currentTime = Date.now();
        return Math.max(expiryTime - currentTime, 0);
      }
      return 0;
    };

    const checkTimeIntervals = (timeLeft) => {
      const oneHour = 60 * 60 * 1000;
      const halfHour = 30 * 60 * 1000;
      const tenMin = 10 * 60 * 1000;

      if (timeLeft <= oneHour && !hourToastShown) {
        toast.info('You have 1 hour left in your session. Click the Clock To Refresh.', { autoClose: false });
        setClockColor('#F18F01');
        setHourToastShown(true);
      }
      if (timeLeft <= halfHour && !halfHourToastShown) {
        toast.warning('You have 30 minutes left in your session. Click the Clock To Refresh.', { autoClose: false });
        setClockColor('yellow');
        setHalfHourToastShown(true);
      }
      if (timeLeft <= tenMin && !tenMinToastShown) {
        toast.error('You have 10 minutes left in your session. Click the Clock To Refresh.', { autoClose: false });
        setClockColor('red');
        setTenMinToastShown(true);
      }
    };

    const updateTimer = () => {
      const timeLeft = calculateTimeLeft();
      setLocalTimeLeft(timeLeft);
      checkTimeIntervals(timeLeft);
      if (timeLeft <= 0) navigate('/session-expired');
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [navigate, hourToastShown, halfHourToastShown, tenMinToastShown]);

  const formatTime = (ms) => {
    if (ms <= 0) return '0:00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleRefreshSession = () => {
    user?.refreshSession();
    setClockColor('#007A7A');
  };

  const handleOpenPreferencesModal = () => setIsPreferencesModalOpen(true);
  const handleClosePreferencesModal = () => setIsPreferencesModalOpen(false);
  const handleOpenUserDetailsModal = () => setIsUserDetailsModalOpen(true);
  const handleCloseUserDetailsModal = () => setIsUserDetailsModalOpen(false);
  const handleOpenPhoneModal = () => setPhoneModalOpen(true);
  const handleClosePhoneModal = () => setPhoneModalOpen(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* AppBar with logo, title, clock, phone */}
      <AppBar position="static">
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 56, px: 2, position: 'relative' }}>
          {/* Logo */}
          <Box sx={{ position: 'absolute', top: '15px', left: '20px', display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src="./bankers-surety-logo.png"
              alt="Logo"
              sx={{ height: 30 }}
            />
          </Box>

          {/* Centered Title */}
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="h6" sx={{ color: '#007A7A', fontWeight: 'bold', fontSize: '1.55rem' }}>
              Bankers Surety
            </Typography>
            <Typography variant="h6" sx={{ color: '#F18F01', fontWeight: 'lighter', fontSize: '1.55rem' }}>
              Agent Portal
            </Typography>
          </Box>

          {/* Clock + Contact Icons */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
            <Tooltip title={`Time Remaining: ${formatTime(localTimeLeft)} Click to Refresh`}>
              <IconButton
                sx={{ color: clockColor, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.2)' } }}
                onClick={handleRefreshSession}
              >
                <WatchLaterIcon fontSize="large" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Contact Information">
              <IconButton
                sx={{ color: 'primary.main', ml: 2, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.2)' } }}
                onClick={handleOpenPhoneModal}
              >
                <PhoneIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        <Box
          sx={{
            width: '80px',
            backgroundColor: '#F4F4F9',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingY: 1,
            borderRight: '3px solid #e0e0e0',
          }}
        >
          <Tooltip title="Back to Home" placement="right">
            <IconButton onClick={() => navigate('/LargeBond')} color="primary">
              <HomeIcon sx={{ fontSize: '2.5rem' }} />
            </IconButton>
          </Tooltip>

          {/* <Tooltip title="Contacts and Preferences" placement="right">
            <IconButton onClick={handleOpenPreferencesModal} color="secondary">
              <ContactPhoneIcon sx={{ fontSize: '2.5rem' }} />
            </IconButton>
          </Tooltip> */}

          <Tooltip title="User Details" placement="right">
            <IconButton onClick={handleOpenUserDetailsModal} color="primary">
              <AccountCircleIcon sx={{ fontSize: '2.5rem' }} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Back to Main Portal" placement="right">
            <IconButton onClick={() => (window.location.href = process.env.REACT_APP_PORTAL_URL)} color="secondary">
              <LanguageIcon sx={{ fontSize: '2.5rem' }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, padding: 2 }}>
          {children}
        </Box>
      </Box>

      {/* Modals */}
      <PreferencesModal
        isOpen={isPreferencesModalOpen}
        onClose={handleClosePreferencesModal}
        agencies={user?.info?.agencyDetails}
      />
      <UserDetailsModal
        isOpen={isUserDetailsModalOpen}
        onClose={handleCloseUserDetailsModal}
      />

      {/* Phone Modal */}
      <Modal open={phoneModalOpen} onClose={handleClosePhoneModal}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 600,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 8,
          p: 4,
          maxHeight: '80vh',
          overflowY: 'auto',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography id="contact-info-title" variant="h6">Contact Information</Typography>
            <IconButton onClick={handleClosePhoneModal}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Large Bond Support</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2"><strong>Toll Free:</strong> 877-778-5245</Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Email</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2"><strong>Email:</strong> largebonds@bankersinsurance.com</Typography>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default HubLayout;
